import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageSquare, ArrowUpRight, CheckCircle2 } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { COMPANY } from "../lib/data";

const FORMSPREE_API = "https://formspree.io/f/myklajen";
const PROJECT_TYPES = [
  "Bank Branch",
  "Insurance Office",
  "Financial Services Office",
  "Corporate Office",
  "Retail / Telecom Store",
  "Renovation / Refurbishment",
  "Other Commercial",
  "Residential",
  "Others",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    project_type: "Others",
    location: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill all required fields.");
      return;
    }
    setLoading(true);
    try {
      const composedMessage = `Project Type: ${form.project_type}\nLocation: ${form.location || "N/A"}\n\n${form.message}`;
      await axios.post(FORMSPREE_API, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        company: form.company,
        message: composedMessage,
      });
      toast.success("Inquiry received. We'll be in touch within 24 hours.");
      setDone(true);
    } catch (err) {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader
        eyebrow={<span className="text-yellow-400">Get in touch</span>}
        title={<span className="text-yellow-400">Tell us about your project.</span>}
        sub="Share your requirement and our team will revert within 24 hours with next steps."
        image="https://images.unsplash.com/photo-1497366811353-6870744d04b2?auto=format&fit=crop&w=2400&q=85"
      />

      <section className="bg-[#FAFAFA] py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Info */}
          <aside className="lg:col-span-5 space-y-5">
            <div className="bg-white border border-[#E2E8F0] p-7">
              <div className="flex items-start gap-4">
                <span className="w-11 h-11 flex items-center justify-center bg-[#0A192F] text-[#C5A059] shrink-0">
                  <Phone size={18} strokeWidth={1.5} />
                </span>
                <div>
                  <p className="text-xs tracking-[0.25em] uppercase text-[#C5A059]">Call</p>
                  {COMPANY.phones.map((p) => (
                    <a key={p} href={`tel:${p.replace(/\s+/g, "")}`} data-testid={`contact-phone-${p}`} className="font-heading text-lg font-semibold text-[#0A192F] block mt-1 hover:text-[#C5A059] transition-colors">
                      {p}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-white border border-[#E2E8F0] p-7">
              <div className="flex items-start gap-4">
                <span className="w-11 h-11 flex items-center justify-center bg-[#0A192F] text-[#C5A059] shrink-0">
                  <Mail size={18} strokeWidth={1.5} />
                </span>
                <div>
                  <p className="text-xs tracking-[0.25em] uppercase text-[#C5A059]">Email</p>
                  <div className="mt-1 flex flex-col gap-1">
                    {COMPANY.emails.map((e) => (
                      <a key={e} href={`mailto:${e}`} data-testid={`contact-email-${e}`} className="font-heading text-lg font-semibold text-[#0A192F] hover:text-[#C5A059] transition-colors break-all">
                        {e}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <a href={`https://wa.me/${COMPANY.whatsapp}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="block bg-white border border-[#E2E8F0] p-7 hover:border-[#C5A059] transition-colors">
              <div className="flex items-start gap-4">
                <span className="w-11 h-11 flex items-center justify-center bg-[#25D366] text-white shrink-0">
                  <MessageSquare size={18} strokeWidth={1.5} />
                </span>
                <div>
                  <p className="text-xs tracking-[0.25em] uppercase text-[#C5A059]">WhatsApp</p>
                  <p className="font-heading text-lg font-semibold text-[#0A192F] mt-1">Chat with our team</p>
                </div>
              </div>
            </a>

            <div className="bg-white border border-[#E2E8F0] p-7">
              <div className="flex items-start gap-4">
                <span className="w-11 h-11 flex items-center justify-center bg-[#0A192F] text-[#C5A059] shrink-0">
                  <MapPin size={18} strokeWidth={1.5} />
                </span>
                <div className="space-y-4">
                  <div>
                    <p className="text-xs tracking-[0.25em] uppercase text-[#C5A059]">{COMPANY.hq.label}</p>
                    <p className="text-[#0A192F] mt-1 leading-relaxed">{COMPANY.hq.address}</p>
                  </div>
                  <div>
                    <p className="text-xs tracking-[0.25em] uppercase text-[#C5A059]">{COMPANY.branch.label}</p>
                    <p className="text-[#0A192F] mt-1 leading-relaxed whitespace-pre-line">{COMPANY.branch.address}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-[#E2E8F0] overflow-hidden">
              <iframe
                title="Map"
                src="https://www.google.com/maps?q=Ballygunj+Gardens,+Kolkata&output=embed"
                className="w-full h-64"
                loading="lazy"
              />
            </div>
          </aside>

          {/* Form */}
          {done ? (
            <div data-testid="contact-success" className="lg:col-span-7 bg-white border border-[#E2E8F0] p-12 text-center flex flex-col items-center">
              <CheckCircle2 size={48} className="text-[#C5A059]" strokeWidth={1.5} />
              <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-6">Inquiry received.</h2>
              <p className="mt-4 text-[#475569] max-w-md">
                Thank you for reaching out to Alex Interiors. Our team will respond within 24 hours.
              </p>
              <button
                onClick={() => { setDone(false); setForm({ name:"", email:"", phone:"", company:"", project_type: "Others", location:"", message:"" }); }}
                data-testid="contact-new"
                className="mt-8 inline-flex items-center gap-2 border border-[#0A192F] text-[#0A192F] px-6 py-3 text-sm hover:bg-[#0A192F] hover:text-white transition-colors"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="lg:col-span-7 bg-white border border-[#E2E8F0] p-8 lg:p-12 space-y-6">
              <div>
                <p className="eyebrow">Send us a message</p>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-3 leading-tight text-yellow-400">
                  Tell us about your project.
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field label="Name *">
                  <input required value={form.name} onChange={update("name")} className={inputCls} data-testid="contact-input-name" />
                </Field>
                <Field label="Company">
                  <input value={form.company} onChange={update("company")} className={inputCls} data-testid="contact-input-company" />
                </Field>
                <Field label="Email *">
                  <input type="email" required value={form.email} onChange={update("email")} className={inputCls} data-testid="contact-input-email" />
                </Field>
                <Field label="Phone *">
                  <input required value={form.phone} onChange={update("phone")} className={inputCls} data-testid="contact-input-phone" />
                </Field>
                <Field label="Project type">
                  <select value={form.project_type} onChange={update("project_type")} className={inputCls} data-testid="contact-input-type">
                    {PROJECT_TYPES.map((p) => <option key={p}>{p}</option>)}
                  </select>
                </Field>
                <Field label="Location / city">
                  <input value={form.location} onChange={update("location")} className={inputCls} data-testid="contact-input-location" placeholder="e.g. Kolkata, WB" />
                </Field>
              </div>

              <Field label="Message *">
                <textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  className={`${inputCls} resize-none`}
                  data-testid="contact-input-message"
                  placeholder="Scope, area, timeline, special requirements…"
                />
              </Field>

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="inline-flex items-center gap-3 bg-[#0A192F] text-white px-8 py-4 text-sm font-semibold tracking-wide hover:bg-[#C5A059] hover:text-[#0A192F] transition-colors disabled:opacity-50"
              >
                {loading ? "Sending…" : "Send Message"} <ArrowUpRight size={16} />
              </button>
            </form>
          )}
        </div>
      </section>
    </>
  );
}

const inputCls =
  "w-full bg-transparent border-b border-[#E2E8F0] focus:border-[#C5A059] outline-none py-3 text-[#0A192F]";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">{label}</span>
      {children}
    </label>
  );
}
